import React from "react"
import {useTranslation} from "react-i18next"

import {GenericErrorAlert} from "../../../../components/Alert.tsx"
import Button from "../../../../components/Button.tsx"
import {InputBase} from "../../../../components/fields/Input.tsx"
import Pagination from "../../../../components/Pagination.tsx"
import {usePageSize} from "../../../../components/RowCountPicker.tsx"
import {Table} from "../../../../components/Table/Table.tsx"
import {TableNoRows} from "../../../../components/Table/TableNoRows.tsx"
import {TableRow} from "../../../../components/Table/TableRow.tsx"
import {getColumnsFromMeta} from "../../../../components/Table/utils/columns.ts"
import {TColumnsMetaWithEmpty} from "../../../../components/Table/utils/shared.ts"
import {useLSTableColumnsState} from "../../../../components/Table/utils/useLSTableColumnsState.tsx"
import {useSaaSSalesPersonSalesAssistantDealsQuery} from "../../../../queries/salesAssistant.ts"
import {useUserSettingsOrLogout} from "../../../../queries/user.ts"
import {ASalesAssistantDeal, ASalesAssistantTaskStatuses} from "../../../../services/types.generated.ts"
import {EMPTY_ARRAY} from "../../../../utils"
import {CombineProviders} from "../../../../utils/context.tsx"
import {isSaaSCompanyUser, isSaaSSalespersonUser} from "../../../../utils/types.ts"
import {DealDetailFlyout} from "../../components/DealDetailFlyout.tsx"
import {MarkAsDoneButton} from "../../components/MarkAsDoneButton.tsx"
import {PlanActivity} from "../../components/PlanActivity.tsx"
import {PlannedActivityCell} from "../../components/PlannedActivityCell.tsx"
import {PriorityBadgeCell} from "../../components/PriorityBadgeCell.tsx"
import {
  SalesAssistantDealDetailContext,
  SalesAssistantDealsFilteringContext,
  useSalesAssistantDealsFiltering,
  useSalesAssistantSalesPersonFilter,
} from "../../hooks.ts"
import {TSalesAssistantDealsTableColumn} from "../../utils.ts"
import {PlannedActivityFilter, PriorityFilter} from "./Filters.tsx"

const tableId = "sales assistant deals"
const PAGE_SIZE = 10

export const DealsSection: React.FC = () => {
  const {t} = useTranslation()
  const {user} = useUserSettingsOrLogout()

  const dealsFiltering = SalesAssistantDealsFilteringContext.useProviderValue(useSalesAssistantDealsFiltering())
  const dealDetailContextValue = SalesAssistantDealDetailContext.useProviderValue(null)

  const {
    orderBy,
    setOrderBy,
    searchString,
    searchStringDebounced,
    setSearchString,
    priorityFilter,
    setPriorityFilter,
    priorityFilterDebounced,
    plannedActivityFilter,
    setPlannedActivityFilter,
    plannedActivityFilterDebounced,
  } = dealsFiltering

  const [salesPersonExternalId] = useSalesAssistantSalesPersonFilter()

  const {data, error, pagination, isFetching, refetch} = useSaaSSalesPersonSalesAssistantDealsQuery({
    orderBy,
    pageSize: usePageSize(tableId, PAGE_SIZE),
    searchString: searchStringDebounced,
    priorities: priorityFilterDebounced.map(opt => opt.value),
    plannedActivitiesTypes: plannedActivityFilterDebounced.map(opt => opt.value),
    salesPersonExternalId,
  })

  const deals = (data?.data.deals ?? EMPTY_ARRAY) as ASalesAssistantDeal[]
  const handleClickRow = React.useMemo(() => {
    return (row: ASalesAssistantDeal) => {
      return () => dealDetailContextValue.setValue(row)
    }
  }, [dealDetailContextValue])

  const saasCompanySalesPersonId = isSaaSCompanyUser(user) ? user.sales_assistant_sales_person?.id : undefined
  const showActionsColumn = !isSaaSCompanyUser(user) || saasCompanySalesPersonId != null

  const columnsMeta = React.useMemo<TColumnsMetaWithEmpty<TSalesAssistantDealsTableColumn, ASalesAssistantDeal>>(
    () =>
      [
        isSaaSCompanyUser(user) && {
          column: "sales_person",
          HeaderCellValue: () => t("SalesAssistant_Deals_Table_SalesPerson"),
          CellValue: ({row}) => (
            <span className={"font-bold"}>
              {[row.sales_person?.first_name, row.sales_person?.last_name].filter(Boolean).join(" ")}
            </span>
          ),
        },
        {
          column: "title",
          sortFn: true,
          HeaderCellValue: () => t("SalesAssistant_Deals_Table_Organization"),
          CellValue: ({row}) => <span className={"font-bold"}>{row.title}</span>,
        },
        {
          column: "priority",
          HeaderCellValue: () => t("SalesAssistant_Deals_Table_Priority"),
          CellValue: PriorityBadgeCell,
          isFiltered: () => priorityFilter.length > 0,
          clearFilter: () => setPriorityFilter([]),
          FilterContent: PriorityFilter,
        },
        {
          column: "planned_activity",
          size: "max-content",
          HeaderCellValue: () => t("SalesAssistant_Deals_Table_PlannedActivity"),
          isFiltered: () => plannedActivityFilter.length > 0,
          clearFilter: () => setPlannedActivityFilter([]),
          CellValue: PlannedActivityCell,
          FilterContent: PlannedActivityFilter,
        },
        {
          column: "selling_title",
          sortFn: true,
          HeaderCellValue: () => t("SalesAssistant_Deals_Table_Product"),
          CellValue: ({row}) => <span className={"font-bold"}>{row.selling_title}</span>,
        },
        showActionsColumn && {
          column: "actions",
          HeaderCellValue: () => t("SalesAssistant_Deals_Table_Actions"),
          CellValue: ({row}) => {
            const canActOnRow = !isSaaSCompanyUser(user) || row.sales_person?.id === saasCompanySalesPersonId

            if (!canActOnRow) {
              return null
            }

            return (
              <div className={"flex items-center gap-2"}>
                {!(
                  (isSaaSCompanyUser(user) || isSaaSSalespersonUser(user)) &&
                  user.company_sync_organizations_as_deals
                ) && (
                  <Button color={"blue"} size={"sm"} variant={"light"} onClick={handleClickRow?.(row)}>
                    {t("SalesAssistant_Deals_Table_Details")}
                  </Button>
                )}
                {row.planned_task && row.planned_task.status == ASalesAssistantTaskStatuses.NotStarted && (
                  <MarkAsDoneButton plannedTask={row.planned_task} organization={row.title} />
                )}
                {!row.planned_task && <PlanActivity deal={row} />}
              </div>
            )
          },
        },
      ] as const,
    [
      t,
      priorityFilter.length,
      setPriorityFilter,
      plannedActivityFilter.length,
      setPlannedActivityFilter,
      handleClickRow,
      user,
      showActionsColumn,
      saasCompanySalesPersonId,
    ]
  )

  const columnsState = useLSTableColumnsState(tableId, {
    columnsOrder: getColumnsFromMeta(columnsMeta),
    pinnedColumn: null,
  })

  if (error) {
    return (
      <DealsCard>
        <GenericErrorAlert retry={refetch} />
      </DealsCard>
    )
  }

  return (
    <CombineProviders
      providers={[
        SalesAssistantDealsFilteringContext.combined(dealsFiltering),
        SalesAssistantDealDetailContext.combined(dealDetailContextValue),
      ]}
    >
      <DealsCard>
        <div className={"flex flex-wrap-reverse items-center justify-between gap-6"}>
          <h2 className={"text-2xl font-bold"}>{t("SalesAssistant_Title_Deals")}</h2>
          <InputBase
            value={searchString}
            onChange={e => setSearchString(e.target.value)}
            placeholder={t("SearchPlaceholder")}
          />
        </div>
        <div className={"flex grow flex-col"}>
          <Table<TSalesAssistantDealsTableColumn, ASalesAssistantDeal>
            {...columnsState}
            data={deals}
            loading={isFetching}
            orderBy={orderBy}
            onOrder={setOrderBy}
            ghost
            columnsMeta={columnsMeta}
          >
            {({data, pinnedColumn}) => (
              <>
                {data.length > 0 &&
                  data.map((row, index) => (
                    <TableRow
                      key={row.id}
                      testId={`row-${row.id}`}
                      row={row}
                      rowIndex={index}
                      className={"whitespace-pre-wrap"}
                      pinnedColumn={pinnedColumn}
                    />
                  ))}
                {data.length === 0 && !isFetching && (
                  <TableNoRows>{t("SalesAssistant_Deals_Table_NoDeals")}</TableNoRows>
                )}
              </>
            )}
          </Table>
        </div>
        <Pagination {...pagination} />
      </DealsCard>
      <DealDetailFlyout />
    </CombineProviders>
  )
}

const DealsCard: React.FC<{children: React.ReactNode}> = ({children}) => {
  return <div className={"flex flex-col gap-6 card-shadow p-6"}>{children}</div>
}
